/**
 * ContextTabs — horizontal context-switcher strip rendered below OperatorTopbar.
 *
 * Doctrine: these are "context" destinations the user flips between many times
 * an hour (Sales Path, Question Bank, Methodology, Manuals, Accounts). Placing
 * them in a top horizontal strip rather than the left rail reduces sidebar
 * congestion and matches the dual-navigation pattern Bryan requested.
 *
 * Height: 40px. Active tab gets a 2px red (#DC2626) bottom underline.
 * Inactive tabs use muted gray text.
 */
import { useLocation } from "wouter";
import { tokens } from "./tokens";

export type ContextTab = {
  label: string;
  href: string;
  /** Optional additional hrefs that should also trigger the active state */
  matchPrefixes?: string[];
};

export const CONTEXT_TABS: ContextTab[] = [
  { label: "Accounts",     href: "/accounts",     matchPrefixes: ["/accounts"] },
  { label: "Question Bank", href: "/question-bank", matchPrefixes: ["/question-bank"] },
  { label: "Methodology",  href: "/methodology",  matchPrefixes: ["/methodology"] },
  { label: "Flowcharts",   href: "/flowcharts",   matchPrefixes: ["/flowcharts"] },
  { label: "Manuals",      href: "/manuals",      matchPrefixes: ["/manuals"] },
];

interface ContextTabsProps {
  tabs?: ContextTab[];
}

export default function ContextTabs({ tabs = CONTEXT_TABS }: ContextTabsProps) {
  const [location, setLocation] = useLocation();

  function isActive(tab: ContextTab): boolean {
    if (location === tab.href) return true;
    if (tab.matchPrefixes) {
      return tab.matchPrefixes.some(
        (prefix) => location === prefix || location.startsWith(prefix + "/")
      );
    }
    return location.startsWith(tab.href + "/");
  }

  return (
    <div
      role="tablist"
      aria-label="Context navigation"
      className="aip-context-tabs"
      style={{
        height: tokens.space.contextTabsHeight,
        background: "#FFFFFF",
        borderBottom: `1px solid ${tokens.color.border}`,
        display: "flex",
        alignItems: "stretch",
        paddingRight: 24,
        gap: 0,
        position: "sticky",
        top: tokens.space.topbarHeight,
        zIndex: 29,
        fontFamily: tokens.font.body,
        overflowX: "auto",
        scrollbarWidth: "none",
      }}
    >
      {tabs.map((tab) => {
        const active = isActive(tab);
        return (
          <button
            key={tab.href}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => setLocation(tab.href)}
            style={{
              position: "relative",
              padding: "0 14px",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: active ? 600 : 400,
              color: active ? tokens.color.textPrimary : tokens.color.textMuted,
              letterSpacing: active ? "-0.01em" : "0",
              whiteSpace: "nowrap",
              transition: "color 0.12s",
              outline: "none",
            }}
            onMouseEnter={(e) => {
              if (!active) {
                (e.currentTarget as HTMLButtonElement).style.color = tokens.color.textPrimary;
              }
            }}
            onMouseLeave={(e) => {
              if (!active) {
                (e.currentTarget as HTMLButtonElement).style.color = tokens.color.textMuted;
              }
            }}
          >
            {tab.label}
            {active && (
              <span
                aria-hidden="true"
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 2,
                  background: tokens.color.red,
                  borderRadius: "1px 1px 0 0",
                }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
